import React, { useState } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Switch,
  Typography,
} from "@mui/material";
import {
  ApartmentOutlined as BedroomsIcon,
  LocationOnOutlined as LocationIcon,
  LocalOfferOutlined as LeadTypeIcon,
  PercentOutlined as PercentIcon,
} from "@mui/icons-material";
import BedroomsAllocation from "./Bedroomsallocation";
import AreaAllocation from "./Areaallocation";
import LeadTypeAllocation from "./Leadtypeallocation";
import PercentageSplitAllocation from "./Percentagesplitallocation";



interface RuleType {
  id: string;
  label: string;
  icon: any;
  priority: number;
  active: boolean;
}

const RULE_TYPES: RuleType[] = [
  { id: "bedrooms",   label: "Bedrooms",         icon: BedroomsIcon, priority: 1, active: true },
  { id: "area",       label: "Area / Postcode",  icon: LocationIcon, priority: 2, active: true },
  { id: "leadType",   label: "Lead Type",        icon: LeadTypeIcon, priority: 3, active: false },
  { id: "percentage", label: "Percentage Split", icon: PercentIcon,  priority: 4, active: false },
];

const LeadAllocationPage = () => {
  const [ruleTypes, setRuleTypes] = useState<RuleType[]>(RULE_TYPES);
  const [activeTab, setActiveTab] = useState("bedrooms");

  const handleToggleActive = (id: string) => {
    setRuleTypes((prev) =>
      prev.map((r) => (r.id === id ? { ...r, active: !r.active } : r))
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case "bedrooms":   return <BedroomsAllocation />;
      case "area":       return <AreaAllocation />;
      case "leadType":   return <LeadTypeAllocation />;
      case "percentage": return <PercentageSplitAllocation />;
      default:           return null;
    }
  };

  return (
    <Box sx={{ padding: "24px 10px" }}>
      {/* Page Title */}
      <Typography
        variant="h6"
        sx={{ fontWeight: 700, color: "#333", fontSize: "18px", marginBottom: "4px" }}
      >
        Lead Allocation Rules
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: "#888", fontSize: "12px", display: "block", marginBottom: "20px" }}
      >
        Automatically assign incoming leads to sales users based on predefined rules.
      </Typography>

      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        {/* ── Sidebar ──────────────────────────────────────────────── */}
        <Paper
          sx={{
            width: "210px",
            padding: "12px",
            borderRadius: "8px",
            flexShrink: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: "#333",
              display: "block",
              fontSize: "12px",
              marginBottom: "8px",
            }}
          >
            Lead Allocation Rules
          </Typography>

          <List sx={{ padding: 0 }}>
            {ruleTypes.map((rule) => {
              const IconComponent = rule.icon;
              const isSelected = activeTab === rule.id;

              return (
                <ListItem
                  key={rule.id}
                  button
                  selected={isSelected}
                  onClick={() => setActiveTab(rule.id)}
                  sx={{
                    borderRadius: "6px",
                    marginBottom: "4px",
                    padding: "4px 8px",
                    minHeight: "38px",
                    backgroundColor: isSelected ? "#e8f5e9" : "transparent",
                    borderLeft: isSelected ? "3px solid #02906f" : "3px solid transparent",
                    "&:hover": { backgroundColor: "#f0f0f0" },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: "28px" }}>
                    <IconComponent sx={{ fontSize: "16px", color: "#02906f" }} />
                  </ListItemIcon>

                  <ListItemText
                    primary={rule.label}
                    secondary={`Priority ${rule.priority}`}
                    primaryTypographyProps={{ sx: { fontSize: "12px", fontWeight: 500 } }}
                    secondaryTypographyProps={{ sx: { fontSize: "10px" } }}
                  />

                  <Switch
                    checked={rule.active}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleActive(rule.id);
                    }}
                    sx={{ marginLeft: "4px" }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Paper>

        {/* ── Active Rule Component ─────────────────────────────────── */}
        <Box sx={{ flex: 1 }}>
          {renderContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default LeadAllocationPage;